import { useQuery } from "@tanstack/react-query";

import { findProspect, getSessionAndSale } from "./dealsCrmActions";
import { fetchAirtableFields } from "./leadEnrich";
import { leadKey } from "./leadUtils";
import type { DealLead } from "./types";

export interface LeadDetail {
  prospect: Record<string, any> | null;
  activities: {
    type: string;
    title: string;
    starts_at: string;
    completed_at: string | null;
  }[];
  notes: { body: string; created_at: string }[];
  airtableFields: Record<string, any> | null;
}

/**
 * Modal data: in parallel, look up an existing prospect (Supabase, by
 * source_record_id) with its activities/notes, and the fresh Airtable record.
 */
export const useLeadDetail = (item: DealLead | null) =>
  useQuery({
    queryKey: ["deals", "leadDetail", item ? leadKey(item) : "none"],
    enabled: !!item,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<LeadDetail> => {
      const lead = item!;
      const out: LeadDetail = {
        prospect: null,
        activities: [],
        notes: [],
        airtableFields: null,
      };
      await Promise.all([
        (async () => {
          const ctx = await getSessionAndSale();
          if (!ctx) return;
          out.prospect = await findProspect(ctx, lead);
          if (!out.prospect) return;
          const [acts, notes] = await Promise.all([
            ctx.sb
              .from("activities")
              .select("type,title,starts_at,completed_at")
              .eq("prospect_id", out.prospect.id)
              .order("starts_at", { ascending: true }),
            ctx.sb
              .from("prospect_notes")
              .select("body,created_at")
              .eq("prospect_id", out.prospect.id)
              .order("created_at", { ascending: false }),
          ]);
          out.activities = acts.data ?? [];
          out.notes = notes.data ?? [];
        })(),
        (async () => {
          try {
            out.airtableFields = await fetchAirtableFields(lead);
          } catch (e) {
            console.warn("lead detail fetch failed", e);
          }
        })(),
      ]);
      return out;
    },
  });
